import { API_BASE_URL } from './auth';
import {
  CommissionStatus,
  PartnerCategory,
  PartnerStatus,
  PartnerTier,
  ReferralStatus,
} from '../constants/partnerNetwork';

/**
 * Partner portal client.
 *
 * Deliberately its own token and its own fetch wrapper rather than reusing the
 * admin ones: a partner and a staff member can be signed in on the same
 * browser, and sharing a key would have one silently sign the other out.
 */

const TOKEN_KEY = 'nexa_partner_token';

export const getPartnerToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setPartnerToken = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

export const clearPartnerToken = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

const partnerHeaders = (): Record<string, string> => {
  const token = getPartnerToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * A 401 means the session is gone, so the token is dropped straight away
 * rather than left to fail on every later call. A 403 is different — the
 * account exists but is pending or suspended — so the token is kept and the
 * message is surfaced.
 */
const partnerFetch = async (path: string, init: RequestInit = {}): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...partnerHeaders(),
      ...(init.headers || {}),
    },
  });

  const data = await response.json().catch(() => ({}));

  if (response.status === 401) {
    clearPartnerToken();
    throw new Error(data.error || 'Your session has expired. Sign in again.');
  }

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
};

/* ------------------------------------------------------------------ */
/* Account                                                             */
/* ------------------------------------------------------------------ */

export interface PartnerProfile {
  id: string;
  partnerId: string;
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  website: string;
  linkedinUrl: string;
  category: PartnerCategory | null;
  tier: PartnerTier | null;
  status: PartnerStatus;
  commissionRate: number;
  createdAt: string;
}

export interface RegisterInput {
  fullName: string;
  companyName?: string;
  email: string;
  phone?: string;
  country?: string;
  city?: string;
  website?: string;
  linkedinUrl?: string;
  category?: PartnerCategory | '';
  password: string;
  confirmPassword: string;
  agreeToTerms: boolean;
}

export const registerPartner = async (
  input: RegisterInput
): Promise<{ message: string; status: PartnerStatus }> => {
  const response = await fetch(`${API_BASE_URL}/api/partner-auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Could not complete your registration');
  }

  return data;
};

export const loginPartner = async (
  email: string,
  password: string
): Promise<{ token: string; partner: PartnerProfile }> => {
  const response = await fetch(`${API_BASE_URL}/api/partner-auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data.error || 'Could not sign in');
  }

  return data;
};

export const fetchPartnerMe = async (): Promise<PartnerProfile> => {
  const data = await partnerFetch('/api/partner-auth/me');
  return data.partner;
};

export const changePartnerPassword = (
  currentPassword: string,
  newPassword: string
): Promise<void> =>
  partnerFetch('/api/partner-auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });

export const updatePartnerProfile = async (
  input: Partial<PartnerProfile>
): Promise<PartnerProfile> => {
  const data = await partnerFetch('/api/partner/profile', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return data.partner;
};

/* ------------------------------------------------------------------ */
/* Dashboard + referrals                                               */
/* ------------------------------------------------------------------ */

export interface PartnerReferral {
  id: string;
  referralId: string;
  businessName: string;
  businessType: string;
  country: string;
  city: string;
  website: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  problem: string;
  additionalInformation: string;
  identifiedHow: string;
  status: ReferralStatus;
  statusLabel: string;
  timeline: Array<{ status: ReferralStatus; label: string; createdAt: string }>;
  estimatedValue: number | null;
  projectValue: number | null;
  commissionRate: number;
  potentialCommission: number | null;
  commissionStatus: CommissionStatus | null;
  commissionDueDate: string | null;
  commissionPaidDate: string | null;
  submittedAt: string;
  updatedAt: string;
}

export interface PartnerDashboard {
  referrals: { total: number; active: number; won: number };
  commissions: {
    pending: number;
    paid: number;
    totalEarned: number;
    currency: string;
  };
  recentReferrals: PartnerReferral[];
}

export const getDashboard = (): Promise<PartnerDashboard> =>
  partnerFetch('/api/partner/dashboard');

export const listMyReferrals = (
  filters: { status?: string; search?: string; page?: number; limit?: number } = {}
): Promise<{
  data: PartnerReferral[];
  pagination: { total: number; page: number; limit: number; pages: number };
}> => {
  const search = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      search.set(key, String(value));
    }
  });
  const qs = search.toString();
  return partnerFetch(`/api/partner/referrals${qs ? `?${qs}` : ''}`);
};

export const getMyReferral = async (id: string): Promise<PartnerReferral> => {
  const data = await partnerFetch(`/api/partner/referrals/${id}`);
  return data.referral;
};

export interface ReferralInput {
  businessName: string;
  businessType?: string;
  country?: string;
  city?: string;
  website?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  problem: string;
  additionalInformation?: string;
  identifiedHow?: string;
  estimatedValue?: number | '';
}

export const submitReferral = (
  input: ReferralInput
): Promise<{ message: string; referral: PartnerReferral }> =>
  partnerFetch('/api/partner/referrals', {
    method: 'POST',
    body: JSON.stringify(input),
  });

/* ------------------------------------------------------------------ */
/* Commissions + notifications                                         */
/* ------------------------------------------------------------------ */

export interface PartnerCommission {
  id: string;
  referral: {
    id: string;
    referralId: string;
    businessName: string;
    status: ReferralStatus;
  } | null;
  commissionRate: number;
  eligibleAmount: number;
  commissionAmount: number;
  currency: string;
  status: CommissionStatus;
  dueDate: string | null;
  paidDate: string | null;
  paymentReference: string;
  createdAt: string;
}

export const listMyCommissions = (): Promise<{
  data: PartnerCommission[];
  totals: {
    pending: number;
    due: number;
    paid: number;
    cancelled: number;
    totalEarned: number;
    currency: string;
  };
}> => partnerFetch('/api/partner/commissions');

export interface PartnerNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  readAt: string | null;
  createdAt: string;
}

export const listMyNotifications = (): Promise<{
  data: PartnerNotification[];
  unread: number;
}> => partnerFetch('/api/partner/notifications');

export const markMyNotificationsRead = (ids?: string[]): Promise<void> =>
  partnerFetch('/api/partner/notifications/read', {
    method: 'PATCH',
    body: JSON.stringify({ ids }),
  });
